import { API_BASE_URL } from '../../../../../config';
import type { PneuCheckupDraft, WorkHistoryEntryDraft } from './careerHistorySurveyTypes';
import { formatDustDuration, parseDustDuration } from './dustDurationFormat';
import { PNEU_SECTION_COMPLETE_STEP } from './careerHistorySurveyPneuSteps';

export type WorkHistoryEntryApi = {
  id?: number | null;
  sortOrder?: number | null;
  siteName?: string | null;
  location?: string | null;
  contractor?: string | null;
  companyName?: string | null;
  incharge?: string | null;
  workPerformed?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  dustDuration?: string | null;
};

export type PneuCheckupApi = {
  id?: number | null;
  sequenceNo?: number | null;
  checkupDate?: string | null;
  contractor?: string | null;
  judgement?: string | null;
  area?: string | null;
};

export type WorkDustHistoryProgressPayload = {
  email?: string;
  invitationId?: string | null;
  employeeId?: number | null;
  currentStepId?: string | null;
  sectionWorkHistoryDone?: boolean;
  sectionPneuDone?: boolean;
  pneuCheckup?: boolean | null;
  pneuCheckups?: PneuCheckupApi[];
  entries?: WorkHistoryEntryApi[];
};

function draftToApi(draft: WorkHistoryEntryDraft, locale: string): WorkHistoryEntryApi {
  const dustDuration =
    draft.dustDurationSkipped || (!draft.dustDurationYears.trim() && !draft.dustDurationMonths.trim())
      ? undefined
      : formatDustDuration(draft.dustDurationYears, draft.dustDurationMonths, locale).trim() ||
        draft.dustDuration.trim() ||
        undefined;
  return {
    id: draft.id ?? undefined,
    sortOrder: draft.sortOrder ?? undefined,
    siteName: draft.siteName.trim() || undefined,
    location: draft.location.trim() || undefined,
    contractor: draft.contractor.trim() || undefined,
    companyName: draft.companyName.trim() || undefined,
    incharge: draft.incharge.trim() || undefined,
    workPerformed: draft.workPerformed.trim() || undefined,
    startDate: draft.startDate || undefined,
    endDate: draft.endDateSkipped ? undefined : draft.endDate || undefined,
    dustDuration,
  };
}

function slotToApi(slot: PneuCheckupDraft['slot1']): PneuCheckupApi {
  return {
    id: slot.id ?? undefined,
    checkupDate: slot.checkupDate || undefined,
    contractor: slot.contractor.trim() || undefined,
    judgement: slot.findings || undefined,
    area: slot.area.trim() || undefined,
  };
}

export function pneuDraftFromApi(
  pneuCheckup: boolean | null | undefined,
  rows: PneuCheckupApi[] | undefined,
  options?: {
    sectionPneuDone?: boolean;
    currentStepId?: string | null;
  }
): PneuCheckupDraft {
  const draft = {
    gateAnswer: '' as const,
    slot1: { id: null, checkupDate: '', contractor: '', findings: '' as const, area: '' },
    slot2: { id: null, checkupDate: '', contractor: '', findings: '' as const, area: '' },
    slot2Skipped: false,
  };
  if (pneuCheckup === true) draft.gateAnswer = 'yes';
  if (pneuCheckup === false) draft.gateAnswer = 'no';
  for (const row of rows ?? []) {
    const slot = row.sequenceNo === 2 ? draft.slot2 : draft.slot1;
    slot.id = row.id ?? null;
    slot.checkupDate = row.checkupDate ?? '';
    slot.contractor = row.contractor ?? '';
    slot.findings = (row.judgement === 'yes' || row.judgement === 'no' ? row.judgement : '') as 'yes' | 'no' | '';
    slot.area = row.area ?? '';
  }
  const hasSlot2 = Boolean(
    draft.slot2.checkupDate || draft.slot2.contractor || draft.slot2.findings || draft.slot2.area
  );
  const sectionDone =
    options?.sectionPneuDone === true || options?.currentStepId === PNEU_SECTION_COMPLETE_STEP;
  draft.slot2Skipped =
    draft.gateAnswer === 'yes' && !hasSlot2 && Boolean(draft.slot1.checkupDate) && sectionDone;
  return draft;
}

export function entryFromApi(row: WorkHistoryEntryApi): WorkHistoryEntryDraft {
  const parsed = parseDustDuration(row.dustDuration ?? '');
  return {
    id: row.id ?? null,
    sortOrder: row.sortOrder ?? null,
    siteName: row.siteName ?? '',
    location: row.location ?? '',
    contractor: row.contractor ?? '',
    companyName: row.companyName ?? '',
    incharge: row.incharge ?? '',
    workPerformed: row.workPerformed ?? '',
    startDate: row.startDate ?? '',
    endDate: row.endDate ?? '',
    endDateSkipped: !row.endDate,
    dustDuration: row.dustDuration ?? '',
    dustDurationYears: parsed.years,
    dustDurationMonths: parsed.months,
    dustDurationSkipped: !row.dustDuration,
  };
}

export async function fetchWorkDustHistoryProgress(
  email: string,
  invitationId?: string
): Promise<WorkDustHistoryProgressPayload | null> {
  if (!API_BASE_URL || !email?.trim()) return null;
  const params = new URLSearchParams({ email: email.trim() });
  if (invitationId?.trim()) params.set('invitationId', invitationId.trim());
  const r = await fetch(`${API_BASE_URL}/api/work-dust-history/progress?${params.toString()}`);
  if (!r.ok) return null;
  return (await r.json()) as WorkDustHistoryProgressPayload;
}

export async function ensureWorkDustHistoryEmployee(params: {
  email: string;
  invitationId?: string;
}): Promise<WorkDustHistoryProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim()) return null;
  const r = await fetch(`${API_BASE_URL}/api/work-dust-history/ensure-employee`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email.trim(),
      invitationId: params.invitationId?.trim() || undefined,
    }),
  });
  if (!r.ok) return null;
  return (await r.json()) as WorkDustHistoryProgressPayload;
}

export async function upsertWorkDustHistoryProgress(params: {
  email: string;
  invitationId?: string;
  currentStepId?: string | null;
  sectionWorkHistoryDone?: boolean;
}): Promise<WorkDustHistoryProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim()) return null;
  const r = await fetch(`${API_BASE_URL}/api/work-dust-history/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email.trim(),
      invitationId: params.invitationId?.trim() || undefined,
      currentStepId: params.currentStepId ?? undefined,
      sectionWorkHistoryDone: params.sectionWorkHistoryDone,
    }),
  });
  if (!r.ok) return null;
  return (await r.json()) as WorkDustHistoryProgressPayload;
}

export async function saveWorkHistoryEntry(params: {
  email: string;
  invitationId?: string;
  draft: WorkHistoryEntryDraft;
  currentStepId?: string | null;
  locale?: string;
}): Promise<WorkDustHistoryProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim()) return null;
  const r = await fetch(`${API_BASE_URL}/api/work-dust-history/entries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email.trim(),
      invitationId: params.invitationId?.trim() || undefined,
      currentStepId: params.currentStepId ?? undefined,
      entry: draftToApi(params.draft, params.locale ?? 'en-US'),
    }),
  });
  if (!r.ok) return null;
  return (await r.json()) as WorkDustHistoryProgressPayload;
}

export async function savePneuCheckupProgress(params: {
  email: string;
  invitationId?: string;
  draft: PneuCheckupDraft;
  currentStepId?: string | null;
  sectionPneuDone?: boolean;
}): Promise<WorkDustHistoryProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim()) return null;
  const gate = params.draft.gateAnswer;
  const pneuCheckup = gate === 'yes' ? true : gate === 'no' ? false : undefined;
  const r = await fetch(`${API_BASE_URL}/api/work-dust-history/pneu-checkups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email.trim(),
      invitationId: params.invitationId?.trim() || undefined,
      currentStepId: params.currentStepId ?? undefined,
      pneuCheckup,
      resetPneuGate: !gate,
      slot1: gate === 'yes' ? slotToApi(params.draft.slot1) : undefined,
      slot2: gate === 'yes' && !params.draft.slot2Skipped ? slotToApi(params.draft.slot2) : undefined,
      slot2Skipped: params.draft.slot2Skipped,
      sectionPneuDone: params.sectionPneuDone ?? params.currentStepId === PNEU_SECTION_COMPLETE_STEP,
    }),
  });
  if (!r.ok) return null;
  return (await r.json()) as WorkDustHistoryProgressPayload;
}
